// src/edu-world/core/barrier-editor.ts
// Polygon-based barrier editor for dev mode.
// Press B to toggle editor mode.
// In editor mode:
//   - Click to place vertices (polygon points)
//   - Each new point connects to the previous with a line
//   - Enter = close polygon (connects last point to first, creates barrier)
//   - Z = undo last point
//   - D = delete last completed polygon
//   - S = export all polygons to console as JSON
//   - Completed polygons shown as semi-transparent red filled shapes
//   - Active polygon shown as yellow lines/dots

import Phaser from 'phaser';
import type { PlayerController } from './player-controller';

type Polygon = { x: number; y: number }[];

export class BarrierEditor {
  private scene: Phaser.Scene;
  private playerController: PlayerController;
  private active: boolean = false;
  private polygons: Polygon[] = [];  // completed polygons
  private currentPoints: Polygon = [];  // points being placed now
  private graphics: Phaser.GameObjects.Graphics;
  private statusText: Phaser.GameObjects.Text;
  private coordText: Phaser.GameObjects.Text;

  private selectedPolygonIndex: number = -1;

  constructor(scene: Phaser.Scene, playerController: PlayerController) {
    this.scene = scene;
    this.playerController = playerController;

    this.graphics = scene.add.graphics();
    this.graphics.setDepth(50);

    // Status text (top-left)
    this.statusText = scene.add.text(10, 10, '', {
      fontSize: '11px',
      fontFamily: 'monospace',
      color: '#ffffff',
      backgroundColor: '#000000cc',
      padding: { x: 6, y: 4 },
    });
    this.statusText.setScrollFactor(0);
    this.statusText.setDepth(200);
    this.statusText.setVisible(false);

    // Coordinate display (follows mouse)
    this.coordText = scene.add.text(0, 0, '', {
      fontSize: '10px',
      fontFamily: 'monospace',
      color: '#ffff00',
      backgroundColor: '#00000088',
      padding: { x: 3, y: 2 },
    });
    this.coordText.setDepth(200);
    this.coordText.setVisible(false);

    // Load saved polygons
    this.loadPolygons();

    // Key bindings
    if (scene.input.keyboard) {
      scene.input.keyboard.on('keydown-B', () => this.toggle());
      scene.input.keyboard.on('keydown-ENTER', () => this.closePolygon());
      scene.input.keyboard.on('keydown-Z', () => this.undoPoint());
      scene.input.keyboard.on('keydown-D', () => this.deleteSelected());
      scene.input.keyboard.on('keydown-S', () => this.exportToConsole());
      scene.input.keyboard.on('keydown-DELETE', () => this.deleteSelected());
      scene.input.keyboard.on('keydown-BACKSPACE', () => this.deleteSelected());
    }

    // Click to place points OR select a polygon
    scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.active) return;
      const worldX = Math.round(pointer.worldX);
      const worldY = Math.round(pointer.worldY);

      // If currentPoints is empty, check if clicking inside an existing polygon (to select it)
      if (this.currentPoints.length === 0) {
        const clickedIdx = this.findPolygonAt(worldX, worldY);
        if (clickedIdx >= 0) {
          this.selectedPolygonIndex = clickedIdx;
          this.redraw();
          this.updateStatus(`Selected polygon #${clickedIdx + 1} — press D/Delete to remove`);
          return;
        }
      }

      // Otherwise, place a new point
      this.selectedPolygonIndex = -1;
      this.currentPoints.push({ x: worldX, y: worldY });
      this.redraw();
    });

    // Show coordinates on mouse move
    scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.active) return;
      this.coordText.setPosition(pointer.worldX + 15, pointer.worldY - 15);
      this.coordText.setText(`${Math.round(pointer.worldX)}, ${Math.round(pointer.worldY)}`);
      this.redraw();
    });
  }

  private toggle(): void {
    this.active = !this.active;
    this.statusText.setVisible(this.active);
    this.coordText.setVisible(this.active);
    if (this.active) {
      this.playerController.setInputLocked(true);
      this.updateStatus();
      this.redraw();
    } else {
      this.playerController.setInputLocked(false);
      this.graphics.clear();
      this.coordText.setVisible(false);
    }
  }

  private closePolygon(): void {
    if (!this.active || this.currentPoints.length < 3) return;
    // Close the polygon and add to completed list
    this.polygons.push([...this.currentPoints]);
    this.currentPoints = [];
    this.applyBarriers();
    this.redraw();
  }

  private undoPoint(): void {
    if (!this.active || this.currentPoints.length === 0) return;
    this.currentPoints.pop();
    this.redraw();
  }

  private deleteSelected(): void {
    if (!this.active) return;

    if (this.selectedPolygonIndex >= 0 && this.selectedPolygonIndex < this.polygons.length) {
      // Delete the selected polygon
      this.polygons.splice(this.selectedPolygonIndex, 1);
      this.selectedPolygonIndex = -1;
      this.applyBarriers();
      this.redraw();
      this.updateStatus('Polygon deleted!');
    } else if (this.polygons.length > 0) {
      // Fallback: delete last polygon if none selected
      this.polygons.pop();
      this.selectedPolygonIndex = -1;
      this.applyBarriers();
      this.redraw();
      this.updateStatus('Last polygon deleted (none was selected)');
    }
  }

  /** Find which polygon contains the point (ray-casting algorithm) */
  private findPolygonAt(x: number, y: number): number {
    for (let idx = this.polygons.length - 1; idx >= 0; idx--) {
      if (this.pointInPolygon(x, y, this.polygons[idx])) {
        return idx;
      }
    }
    return -1;
  }

  /** Ray-casting point-in-polygon test */
  private pointInPolygon(x: number, y: number, poly: Polygon): boolean {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x, yi = poly[i].y;
      const xj = poly[j].x, yj = poly[j].y;
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  }

  private exportToConsole(): void {
    const json = JSON.stringify(this.polygons, null, 2);
    console.log('=== BARRIER POLYGONS JSON ===');
    console.log(json);
    localStorage.setItem('edu-world-barriers-poly', json);

    // Download as file (same pattern as position editor)
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'edu-world-barriers.json';
    a.click();
    URL.revokeObjectURL(url);

    this.updateStatus('Exported! File downloaded: edu-world-barriers.json');
  }

  private loadPolygons(): void {
    const saved = localStorage.getItem('edu-world-barriers-poly');
    if (saved) {
      try {
        this.polygons = JSON.parse(saved);
        this.applyBarriers();
      } catch { /* ignore */ }
    }
  }

  private applyBarriers(): void {
    // Convert polygons to the format the player controller expects
    this.playerController.setPolygonBarriers([...this.polygons]);
    localStorage.setItem('edu-world-barriers-poly', JSON.stringify(this.polygons));
  }

  private redraw(): void {
    this.graphics.clear();
    if (!this.active) return;

    // Draw completed polygons (red, semi-transparent; selected = blue)
    for (let pIdx = 0; pIdx < this.polygons.length; pIdx++) {
      const poly = this.polygons[pIdx];
      const isSelected = pIdx === this.selectedPolygonIndex;
      const color = isSelected ? 0x2196f3 : 0xff0000;
      const alpha = isSelected ? 0.4 : 0.2;

      this.graphics.fillStyle(color, alpha);
      this.graphics.beginPath();
      this.graphics.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) {
        this.graphics.lineTo(poly[i].x, poly[i].y);
      }
      this.graphics.closePath();
      this.graphics.fillPath();

      // Outline
      this.graphics.lineStyle(2, color, isSelected ? 1 : 0.7);
      this.graphics.beginPath();
      this.graphics.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) {
        this.graphics.lineTo(poly[i].x, poly[i].y);
      }
      this.graphics.closePath();
      this.graphics.strokePath();

      // Vertex dots
      for (const pt of poly) {
        this.graphics.fillStyle(color, 0.9);
        this.graphics.fillCircle(pt.x, pt.y, isSelected ? 5 : 4);
      }

      // Index label for selected
      if (isSelected) {
        const cx = poly.reduce((s, p) => s + p.x, 0) / poly.length;
        const cy = poly.reduce((s, p) => s + p.y, 0) / poly.length;
        const lbl = this.scene.add.text(cx, cy, `#${pIdx + 1} ✕`, {
          fontSize: '8px', color: '#ffffff', backgroundColor: '#2196f3',
          padding: { x: 3, y: 2 },
        }).setOrigin(0.5).setDepth(201);
        this.scene.time.delayedCall(100, () => lbl.destroy());
      }
    }

    // Draw current polygon being built (yellow)
    if (this.currentPoints.length > 0) {
      // Lines between placed points
      this.graphics.lineStyle(2, 0xffff00, 0.9);
      this.graphics.beginPath();
      this.graphics.moveTo(this.currentPoints[0].x, this.currentPoints[0].y);
      for (let i = 1; i < this.currentPoints.length; i++) {
        this.graphics.lineTo(this.currentPoints[i].x, this.currentPoints[i].y);
      }
      this.graphics.strokePath();

      // Vertex dots (yellow)
      for (let i = 0; i < this.currentPoints.length; i++) {
        const pt = this.currentPoints[i];
        this.graphics.fillStyle(i === 0 ? 0x00ff00 : 0xffff00, 1);
        this.graphics.fillCircle(pt.x, pt.y, 5);
      }

      // Number labels on vertices
      for (let i = 0; i < this.currentPoints.length; i++) {
        const pt = this.currentPoints[i];
        const numText = this.scene.add.text(pt.x + 8, pt.y - 12, `${i + 1}`, {
          fontSize: '9px', fontFamily: 'monospace', color: '#ffff00',
        }).setDepth(201);
        // Clean up next frame (hacky but simple for dev tool)
        this.scene.time.delayedCall(50, () => numText.destroy());
      }
    }

    this.updateStatus();
  }

  private updateStatus(extra?: string): void {
    let text = `[BARRIER EDITOR] B=toggle | Click=add point or select polygon\n`;
    text += `Enter=close polygon | Z=undo point | D/Delete=remove selected | S=export\n`;
    text += `Polygons: ${this.polygons.length} | Current points: ${this.currentPoints.length}`;
    if (this.selectedPolygonIndex >= 0) text += ` | SELECTED: #${this.selectedPolygonIndex + 1}`;
    if (this.currentPoints.length >= 3) text += ` (Enter to close)`;
    if (extra) text += `\n${extra}`;
    this.statusText.setText(text);
  }
}
